/**
 * Authentication Service
 * Simplified and modernized Firebase Authentication operations
 */

import * as Locales from "@utils/Locales.js";
import { getFirebaseAuth } from "./core/FirebaseApp";

const isClient = typeof window !== "undefined";

import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  signInAnonymously,
  signOut,
  linkWithCredential,
  signInWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  reload,
} from "firebase/auth";

// Re-export providers for use in components
export { GoogleAuthProvider, FacebookAuthProvider, EmailAuthProvider };

// Store auth state listeners
const authStateListeners = new Set();
// Track initialization status
let isAuthServiceInitialized = false;
// Track auth ready state
let authReadyPromise = null;
let authReadyResolve = null;
let isAuthReady = false;
// Track if user was just created (reset after being consumed)
let wasNewUserJustCreated = false;

// Current authentication state (read-only)
let currentAuthState = {
  user: null,
  isReady: false,
  isLoggedIn: false,
  isAnonymous: false,
  hasGoogleProvider: false,
};

/**
 * Initialize the Authentication Service
 * @returns {Promise<void>}
 */
export async function initializeAuthService() {
  if (isAuthServiceInitialized) {
    return true;
  }

  if (!isClient) {
    isAuthServiceInitialized = true;
    if (!authReadyPromise) {
      authReadyPromise = Promise.resolve(null);
    }
    isAuthReady = true;
    return false;
  }

  isAuthServiceInitialized = true;
  const auth = getFirebaseAuth();

  // Set up auth ready promise if not already created
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      authReadyResolve = resolve;
    });
  }

  // Set up persistent auth state monitoring
  onAuthStateChanged(auth, async (user) => {
    // Update current auth state
    updateAuthState(user);

    // Mark auth as ready on first call
    if (!isAuthReady) {
      isAuthReady = true;
      if (authReadyResolve) {
        authReadyResolve(user);
        authReadyResolve = null; // Clear to prevent multiple calls
      }
    }

    // Notify all registered listeners
    notifyAuthStateListeners(user);
  });

  // Handle redirect result if returning from provider
  try {
    await processRedirectResult();
  } catch (e) {
    // Non-fatal; auth state listener will still proceed
    console.warn(
      "processRedirectResult warning:",
      e && e.message ? e.message : e
    );
  }

  return true;
}

/**
 * Update the current authentication state
 * @param {Object} user - Firebase user object
 */
function updateAuthState(user) {
  currentAuthState = {
    user: user,
    isReady: true,
    isLoggedIn: !!user && !user.isAnonymous,
    isAnonymous: !!user && user.isAnonymous,
    hasGoogleProvider:
      !!user &&
      user.providerData.some(
        (p) => p.providerId === GoogleAuthProvider.PROVIDER_ID
      ),
  };
}

/**
 * Get current authentication state (read-only)
 * @returns {Object} - Current auth state
 */
export function getAuthState() {
  return { ...currentAuthState };
}

/**
 * Get current user (synchronous)
 * @returns {Object|null} - Current user or null
 */
export function getCurrentUser() {
  return currentAuthState.user;
}

/**
 * Check if authentication is ready (synchronous)
 * @returns {boolean} - True if auth is ready
 */
export function isAuthenticationReady() {
  return currentAuthState.isReady;
}

/**
 * Check if user is logged in (synchronous)
 * @returns {boolean} - True if user is logged in and not anonymous
 */
export function isUserLoggedIn() {
  return currentAuthState.isLoggedIn;
}

/**
 * Check if user is anonymous (synchronous)
 * @returns {boolean} - True if user is anonymous
 */
export function isUserAnonymous() {
  return currentAuthState.isAnonymous;
}

/**
 * Check if user has Google provider (synchronous)
 * @returns {boolean} - True if user has Google provider linked
 */
export function userHasGoogleProvider() {
  return currentAuthState.hasGoogleProvider;
}

/**
 * Register a listener for auth state changes
 * @param {Function} listener - Callback function that receives the user object
 * @returns {Function} - Function to unregister the listener
 */
export function addAuthStateListener(listener) {
  if (!isClient) {
    return () => {};
  }
  authStateListeners.add(listener);

  // Call immediately with current state if available
  const auth = getFirebaseAuth();
  if (auth.currentUser !== null) {
    try {
      listener(auth.currentUser);
    } catch (error) {
      console.error("Error in auth state listener:", error);
    }
  }

  // Return unsubscribe function
  return () => {
    authStateListeners.delete(listener);
  };
}

/**
 * Notify all registered auth state listeners
 * @param {Object} user - The user object
 */
function notifyAuthStateListeners(user) {
  authStateListeners.forEach((listener) => {
    try {
      listener(user);
    } catch (error) {
      console.error("Error in auth state listener:", error);
    }
  });
}

/**
 * Check if a new user was just created and consume the flag
 * @returns {boolean} - True if user was just created
 */
export function wasUserJustCreated() {
  const result = wasNewUserJustCreated;
  wasNewUserJustCreated = false; // Reset after consuming
  return result;
}

/**
 * Wait for Firebase Auth to be ready
 * @returns {Promise<Object|null>} - Promise that resolves with the current user when auth is ready
 */
export async function waitForAuthReady() {
  if (!isClient) {
    if (!authReadyPromise) {
      authReadyPromise = Promise.resolve(null);
    }
    return authReadyPromise;
  }
  // If auth is already ready, return current user immediately
  if (isAuthReady) {
    const auth = getFirebaseAuth();
    return auth.currentUser;
  }

  // Initialize auth service if not already done
  if (!isAuthServiceInitialized) {
    await initializeAuthService();
  }

  // Wait for the auth ready promise
  return await authReadyPromise;
}

/**
 * Get the currently logged in user (async version that waits for auth ready)
 * @returns {Promise<Object|null>} - Promise that resolves with user object or null if not logged in
 */
export async function getLoggedInUserAsync() {
  if (!isClient) {
    return null;
  }
  await waitForAuthReady();
  const auth = getFirebaseAuth();
  return auth.currentUser;
}

/**
 * Check if a user is logged in (non-anonymously) - async version
 * @returns {Promise<boolean>} - Promise that resolves with true if user is logged in and not anonymous
 */
export async function userIsLoggedInAsync() {
  if (!isClient) {
    return false;
  }
  const user = await getLoggedInUserAsync();
  return !!user && !user.isAnonymous;
}

/**
 * Check if a user is logged in (non-anonymously) - sync version (legacy)
 * @returns {boolean} - True if user is logged in and not anonymous
 * @deprecated Use isUserLoggedIn() instead
 */
export function userIsLoggedIn() {
  return isUserLoggedIn();
}

/**
 * Get the currently logged in user - sync version (legacy)
 * @returns {Object|null} - User object or null if not logged in
 * @deprecated Use getCurrentUser() instead
 */
export function getLoggedInUser() {
  return getCurrentUser();
}

/**
 * Get the current user's ID token
 * @returns {Promise<string|null>} - ID token or null
 */
export async function getUserIdToken() {
  const user = await getLoggedInUserAsync();
  if (user) {
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error("Error getting ID token:", error);
      return null;
    }
  }
  return null;
}

/**
 * Get the current user's UID
 * @returns {Promise<string|null>} - UID or null
 */
export async function getUid() {
  const user = await getLoggedInUserAsync();
  if (user) {
    return user.uid;
  }
  return null;
}

/**
 * Send a verification email to the current user
 * Uses Firebase hosted handler at /__/auth/action by default
 * @returns {Promise<void>}
 */
export async function sendVerificationEmailToCurrentUser() {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently signed in");
  try {
    // Ensure users return through the Firebase handler and then back to our app
    const continueUrl =
      typeof window !== "undefined" && window.location && window.location.origin
        ? `${window.location.origin}/?emailVerified=1`
        : undefined;
    const actionCodeSettings = continueUrl ? { url: continueUrl } : undefined;

    await sendEmailVerification(user, actionCodeSettings);
  } catch (error) {
    console.error("Error sending verification email:", error);
    throw error;
  }
}

/**
 * Reload the current user to refresh claims like emailVerified
 * @returns {Promise<Object|null>} - The refreshed user or null
 */
export async function reloadCurrentUser() {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;
  try {
    await reload(user);
    // Also update internal auth state snapshot
    updateAuthState(auth.currentUser);
    return auth.currentUser;
  } catch (error) {
    console.error("Error reloading current user:", error);
    return auth.currentUser;
  }
}

/**
 * Get anonymous user's ID token if available
 * @returns {Promise<string|null>} - Anonymous token or null
 */
export async function getAnonymousTokenId() {
  const user = await getLoggedInUserAsync();
  if (user && user.isAnonymous) {
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error("Error getting anonymous token:", error);
      return null;
    }
  }
  return null;
}

/**
 * Load the auth UI interface
 * @param {string} elementName - DOM element ID to render the UI
 * @returns {Promise<Object>} - Logged in user
 */
// FirebaseUI widget removed; use redirect/programmatic flows instead.

/**
 * Sign in with a specific provider
 * @param {string} providerName - Provider name (google, facebook)
 * @param {string} locale - Locale for the auth UI
 * @returns {Promise<Object>} - Logged in user
 */

/**
 * Verify that authentication is properly configured for the given provider
 * @param {string} providerName - Provider name (google, facebook)
 * @returns {Promise<boolean>} - True if configuration is valid
 */

/**
 * Sign in anonymously
 * @returns {Promise<Object>} - Anonymous user
 */
export async function logInAnonymously() {
  const auth = getFirebaseAuth();
  try {
    const result = await signInAnonymously(auth);

    return result.user;
  } catch (error) {
    console.error("Error signing in anonymously:", error);
    throw error;
  }
}

/**
 * Check if the current user has a specific provider linked
 * @param {string} providerId - Provider ID to check for (e.g., 'google.com')
 * @returns {Promise<boolean>} - True if provider is linked
 */
export async function hasProviderLinked(providerId) {
  const user = await getLoggedInUserAsync();
  if (!user) return false;

  return user.providerData.some(
    (provider) => provider.providerId === providerId
  );
}

/**
 * Link a Google provider to the current user's account
 * @param {string} locale - Locale for the auth UI
 * @returns {Promise<string>} - Access token from the linked Google account
 */
export async function linkGoogleProvider(locale) {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No user is currently signed in");
  }

  auth.languageCode = Locales.getLoginLocale("Google", locale);
  const provider = getProvider("Google");

  // Add specific scopes for Google Sheets
  provider.addScope("https://www.googleapis.com/auth/drive.readonly");
  provider.addScope("https://www.googleapis.com/auth/spreadsheets.readonly");
  provider.setCustomParameters({ prompt: "select_account" });

  try {
    const result = await signInWithPopup(auth, provider);

    // Link the Google credential to the current user
    if (result.credential) {
      await linkWithCredential(user, result.credential);
      return result.credential.accessToken;
    } else {
      // If no credential in result, try to force a fresh token request
      console.warn(
        "No credential returned from Google link attempt, trying with consent prompt"
      );

      // Force a fresh authentication with consent prompt
      provider.setCustomParameters({ prompt: "consent" });
      const freshResult = await signInWithPopup(auth, provider);
      if (freshResult.credential) {
        await linkWithCredential(user, freshResult.credential);
        return freshResult.credential.accessToken;
      } else {
        throw new Error(
          "Unable to obtain Google credential for linking. Please try signing out and signing in again."
        );
      }
    }
  } catch (error) {
    // Treat user-cancelled popup as benign (no error state)
    if (
      error?.code === "auth/popup-closed-by-user" ||
      error?.code === "auth/cancelled-popup-request"
    ) {
      return null;
    }
    // Handle account exists error by prompting user to sign in with existing method
    if (error.code === "auth/account-exists-with-different-credential") {
      throw new Error(
        "This Google account is already associated with a different user. " +
          "Please sign in with your original email/password first, then try importing sheets again."
      );
    }

    console.error("Error linking Google provider:", error);
    throw error;
  }
}

/**
 * Sign in specifically for Google Sheets access
 * @param {string} locale - Locale for the auth UI
 * @returns {Promise<string>} - Access token
 */
export async function logInToSheets(locale) {
  const auth = getFirebaseAuth();
  const currentUser = auth.currentUser;

  // If user is already signed in, check if they have Google provider linked
  if (currentUser && !currentUser.isAnonymous) {
    const hasGoogle = await hasProviderLinked(GoogleAuthProvider.PROVIDER_ID);

    if (hasGoogle) {
      // User already has Google linked, just get a new token with sheets scopes
      const provider = getProvider("Google");
      provider.addScope("https://www.googleapis.com/auth/drive.readonly");
      provider.addScope(
        "https://www.googleapis.com/auth/spreadsheets.readonly"
      );
      // Use consent prompt to ensure we get fresh credentials
      provider.setCustomParameters({ prompt: "consent" });

      try {
        const result = await signInWithPopup(auth, provider);
        if (result.credential && result.credential.accessToken) {
          return result.credential.accessToken;
        } else {
          throw new Error(
            "Unable to obtain Google access token. This may happen if the popup was blocked or if you cancelled the authentication. Please try again."
          );
        }
      } catch (error) {
        // Treat user-cancelled popup as benign (no error state)
        if (
          error?.code === "auth/popup-closed-by-user" ||
          error?.code === "auth/cancelled-popup-request"
        ) {
          return null;
        }
        if (error.code === "auth/popup-blocked") {
          throw new Error(
            "Popup was blocked by your browser. Please allow popups for this site and try again."
          );
        }
        console.error(
          "Error getting sheets access for existing Google user:",
          error
        );
        throw error;
      }
    } else {
      // User doesn't have Google linked, link it now
      try {
        return await linkGoogleProvider(locale);
      } catch (error) {
        console.error(
          "Error linking Google provider for sheets access:",
          error
        );
        throw error;
      }
    }
  }

  // User is not signed in or is anonymous, proceed with normal Google sign-in
  auth.languageCode = Locales.getLoginLocale("Google", locale);
  const provider = getProvider("Google");

  // Add specific scopes for Google Sheets
  provider.addScope("https://www.googleapis.com/auth/drive.readonly");
  provider.addScope("https://www.googleapis.com/auth/spreadsheets.readonly");

  try {
    // Use consent prompt to ensure we get fresh credentials with access tokens
    provider.setCustomParameters({ prompt: "consent" });
    const result = await signInWithPopup(auth, provider);
    if (result.credential && result.credential.accessToken) {
      return result.credential.accessToken;
    } else {
      throw new Error(
        "Unable to obtain Google access token for sheets access. This may happen if you cancelled the authentication. Please try again."
      );
    }
  } catch (error) {
    // Treat user-cancelled popup as benign (no error state)
    if (
      error?.code === "auth/popup-closed-by-user" ||
      error?.code === "auth/cancelled-popup-request"
    ) {
      return null;
    }
    // Handle specific errors more gracefully
    if (error.code === "auth/popup-blocked") {
      throw new Error(
        "Popup was blocked by your browser. Please allow popups for this site and try again."
      );
    } else if (error.code === "auth/account-exists-with-different-credential") {
      throw new Error(
        "An account with this email already exists. Please sign in with your original email/password first, then try importing sheets again."
      );
    }

    console.error("Error signing in to Google Sheets:", error);
    throw error;
  }
}

/**
 * Sign out the current user
 */
export async function logOut() {
  try {
    const auth = getFirebaseAuth();
    await signOut(auth);
  } catch (error) {
    console.error("Error during logout:", error);
  }
}

/**
 * Reset the Firebase Auth UI
 * @returns {boolean} - Success status
 */
// No UI to reset in redirect-based flow

/**
 * Get the provider instance for a given provider name
 * @param {string} providerName - Provider name (google, facebook)
 * @returns {Object} - Provider instance
 */
function getProvider(providerName) {
  const providers = {
    google: new GoogleAuthProvider(),
    facebook: new FacebookAuthProvider(),
  };
  return providers[providerName.toLowerCase()] || providers["google"];
}

/**
 * Lightweight heuristic to detect mobile-like environments where redirect is preferred
 * - Checks common mobile UA substrings
 * - Falls back to coarse pointer detection
 */
function isMobileLike() {
  try {
    if (typeof navigator !== "undefined") {
      const ua = navigator.userAgent || "";
      const isMobileUA = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
      if (isMobileUA) return true;
    }
    if (typeof window !== "undefined" && window.matchMedia) {
      const coarse = window.matchMedia("(pointer: coarse)").matches;
      if (coarse) return true;
    }
  } catch {}
  return false;
}

/**
 * Smart Google sign-in:
 * - Uses popup on desktop where supported
 * - Falls back to redirect on popup limitations
 * - Uses redirect by default on mobile-like environments
 * @param {string} locale
 * @param {Object} options
 * @param {string[]} [options.scopes]
 * @returns {Promise<Object|null>} user on popup success; null when redirect is initiated
 */
export async function startGoogleSignIn(locale, options = {}) {
  const auth = getFirebaseAuth();
  auth.languageCode = Locales.getLoginLocale("Google", locale);

  const provider = getProvider("Google");
  provider.setCustomParameters({ prompt: "select_account" });

  if (Array.isArray(options.scopes)) {
    options.scopes.forEach((s) => {
      try {
        provider.addScope(s);
      } catch {}
    });
  }

  // Prefer redirect on mobile-like environments
  if (isMobileLike()) {
    await signInWithRedirect(auth, provider);
    return null;
  }

  try {
    const result = await signInWithPopup(auth, provider);
    wasNewUserJustCreated = !!result?.additionalUserInfo?.isNewUser;
    return result.user;
  } catch (error) {
    // Treat user-cancelled popup as benign (no error state)
    if (
      error?.code === "auth/popup-closed-by-user" ||
      error?.code === "auth/cancelled-popup-request"
    ) {
      return null;
    }
    // Fallback to redirect on popup limitations or unsupported environments
    if (
      error?.code === "auth/popup-blocked" ||
      error?.code === "auth/operation-not-supported-in-this-environment"
    ) {
      await signInWithRedirect(auth, provider);
      return null;
    }
    // For other errors, bubble up
    throw error;
  }
}

/**
 * Start Google sign-in with redirect flow
 * @param {string} locale
 */

/**
 * Process redirect result after returning from provider
 * Sets new-user flag when applicable
 * @returns {Promise<Object|null>} user or null if no redirect occurred
 */
export async function processRedirectResult() {
  const auth = getFirebaseAuth();
  try {
    const result = await getRedirectResult(auth);
    if (result && result.user) {
      wasNewUserJustCreated = !!result.additionalUserInfo?.isNewUser;
      try {
        // Signal the app to show the account dialog after returning
        sessionStorage.setItem("showAccountDialogAfterRedirect", "1");
      } catch {}
      return result.user;
    }
    return null;
  } catch (error) {
    console.error("Redirect sign-in failed:", error);
    throw error;
  }
}

/**
 * Email/password sign-in
 */
export async function signInWithEmailPassword(email, password) {
  const auth = getFirebaseAuth();
  const result = await signInWithEmailAndPassword(auth, email, password);
  wasNewUserJustCreated = false;
  return result.user;
}

/**
 * Email/password registration
 */
export async function registerWithEmailPassword(email, password, displayName) {
  const auth = getFirebaseAuth();
  const result = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName && displayName.trim()) {
    try {
      await updateProfile(result.user, { displayName: displayName.trim() });
    } catch {}
  }
  // Send verification email using Firebase hosted handler at /__/auth/action
  try {
    const continueUrl =
      typeof window !== "undefined" && window.location && window.location.origin
        ? `${window.location.origin}/?emailVerified=1`
        : undefined;
    const actionCodeSettings = continueUrl ? { url: continueUrl } : undefined;

    await sendEmailVerification(result.user, actionCodeSettings);
  } catch (e) {
    console.warn("Failed to send verification email after signup:", e);
  }
  wasNewUserJustCreated = true;
  return result.user;
}
